import { useState, useEffect, useCallback } from 'react'
import { InfoTip } from '../../components/InfoTip'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { useSSE } from '../../hooks/useSSE'
import { API_BASE } from '../../utils/api'
import { DesktopOnly, MobileOnly, MobileCard } from '../../components/MobileCard'

interface ClientMargin {
  clientId: string
  netAvailable: number | null
  usedMargin: number | null
  spanMargin: number | null
  exposureMargin: number | null
  fetchedAt: string | null
  loading: boolean
  error?: string
}

interface BrokerClient {
  id: string
  broker_id: string
  client_id: string
  mapped_user_id: string | null
  status: string
  created_at: string
}

interface BrokerData {
  id: string
  name: string
  short_name?: string
  provider: string
  session_token_url?: string
  api_base_url?: string
  iris_ws_url?: string
  apollo_ws_url?: string
  broker_id?: string
  version_no?: string
  iprocli?: string
  xts_interactive_app_key?: string
  xts_interactive_app_secret?: string
  xts_marketdata_app_key?: string
  xts_marketdata_app_secret?: string
  created_at: string
  clients: BrokerClient[]
}

function fmtINR(val: number | null | undefined): string {
  if (val === null || val === undefined) return '—'
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val)
}

export function AdminBrokers() {
  const [brokers, setBrokers] = useState<BrokerData[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)
  // margin state: clientDbId → ClientMargin
  const [margins, setMargins] = useState<Record<string, ClientMargin>>({})

  // delete confirm modal state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: 'broker' | 'client'
    id: string
    brokerId?: string
    label: string      // broker name or client_id — user must type this
    confirmInput: string
  } | null>(null)

  const [showAdvancedBroker, setShowAdvancedBroker] = useState(false)
  const [showAddBroker, setShowAddBroker] = useState(false)
  const [editingBroker, setEditingBroker] = useState<BrokerData | null>(null)
  const [addBrokerForm, setAddBrokerForm] = useState({
    name: '',
    provider: 'xts',
    api_base_url: 'https://symphonyfintech.com/interactive',
    iris_ws_url: 'wss://symphonyfintech.com/marketdata/stream',
    xts_interactive_app_key: '',
    xts_interactive_app_secret: '',
    xts_marketdata_app_key: '',
    xts_marketdata_app_secret: ''
  })

  const [showAddClientFor, setShowAddClientFor] = useState<string | null>(null)
  const [addClientForm, setAddClientForm] = useState({
    client_id: '',
    session_password: '',
    ctcl_password: '',
    algo_id: ''
  })

  // Edit client credentials modal
  const [editingClient, setEditingClient] = useState<{ brokerId: string; clientDbId: string; clientId: string } | null>(null)
  const [editClientForm, setEditClientForm] = useState({ session_password: '', ctcl_password: '', algo_id: '' })

  const { token } = useAuth()
  const { addToast } = useToast()
  const sse = useSSE()

  // Derive WS statuses from SSE broker_status events (real-time from VPS)
  // Maps broker DB id → { irisConnected, apolloConnected, ws: { ... } }
  const wsStatuses: Record<string, any> = Object.fromEntries(
    Object.values(sse.brokerStatuses).map((s: any) => [s.id, s])
  )

  // Fetch margin for a single connected client from Greeksoft MarginDetailRequest
  const fetchMargin = useCallback(async (brokerId: string, clientDbId: string) => {
    setMargins(prev => ({ ...prev, [clientDbId]: { ...prev[clientDbId], clientId: clientDbId, loading: true, fetchedAt: null, netAvailable: null, usedMargin: null, spanMargin: null, exposureMargin: null } }))
    try {
      const res = await fetch(`${API_BASE}/api/brokers/${brokerId}/clients/${clientDbId}/funds`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await res.json()
      if (data.error) {
        setMargins(prev => ({ ...prev, [clientDbId]: { clientId: clientDbId, loading: false, error: data.error, netAvailable: null, usedMargin: null, spanMargin: null, exposureMargin: null, fetchedAt: null } }))
      } else {
        setMargins(prev => ({ ...prev, [clientDbId]: {
          clientId: clientDbId, loading: false, error: undefined,
          netAvailable: data.netAvailable,
          usedMargin: data.usedMargin,
          spanMargin: data.spanMargin,
          exposureMargin: data.exposureMargin,
          fetchedAt: data.fetchedAt || new Date().toISOString(),
        }}))
      }
    } catch {
      setMargins(prev => ({ ...prev, [clientDbId]: { clientId: clientDbId, loading: false, error: 'Fetch failed', netAvailable: null, usedMargin: null, spanMargin: null, exposureMargin: null, fetchedAt: null } }))
    }
  }, [token])

  const fetchBrokers = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/brokers`, { headers: { 'Authorization': `Bearer ${token}` } })
      if (res.ok) setBrokers(await res.json())
    } catch (err) {
      console.warn('API unavailable:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBrokers()
    const interval = setInterval(fetchBrokers, 15000)
    return () => clearInterval(interval)
  }, [token])

  // Auto-refresh margin every 30s for connected clients
  useEffect(() => {
    const refreshAllMargins = () => {
      brokers.forEach(broker => {
        broker.clients
          .filter(c => c.status === 'connected')
          .forEach(c => fetchMargin(broker.id, c.id))
      })
    }
    refreshAllMargins()
    const interval = setInterval(refreshAllMargins, 30000)
    return () => clearInterval(interval)
  }, [brokers, fetchMargin])

  const handleAddBroker = async () => {
    if (!addBrokerForm.name.trim()) return
    try {
      const res = await fetch(`${API_BASE}/api/brokers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(addBrokerForm),
      })
      if (res.ok) {
        const result = await res.json()
        setBrokers(prev => [...prev, { ...addBrokerForm, id: result.id, clients: [], created_at: new Date().toISOString() } as BrokerData])
        setShowAddBroker(false)
        setAddBrokerForm({ name: '', provider: 'xts', api_base_url: 'https://symphonyfintech.com/interactive', iris_ws_url: 'wss://symphonyfintech.com/marketdata/stream', xts_interactive_app_key: '', xts_interactive_app_secret: '', xts_marketdata_app_key: '', xts_marketdata_app_secret: '' })
        addToast(`Broker "${addBrokerForm.name}" created`, 'success')
      } else {
        const errData = await res.json()
        addToast(errData.error || 'Failed to create broker', 'error')
      }
    } catch (err) {
      console.error(err)
      addToast('Network error creating broker', 'error')
    }
  }

  const handleEditBroker = async () => {
    if (!editingBroker) return
    try {
      const res = await fetch(`${API_BASE}/api/brokers/${editingBroker.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(addBrokerForm),
      })
      if (res.ok) {
        setBrokers(prev => prev.map(b => b.id === editingBroker.id ? { ...b, ...addBrokerForm } : b))
        setEditingBroker(null)
        addToast('Broker updated', 'success')
      } else {
        const errData = await res.json()
        addToast(errData.error || 'Failed to update broker', 'error')
      }
    } catch (err) {
      console.error(err)
      addToast('Network error updating broker', 'error')
    }
  }

  const openEditBroker = (broker: BrokerData) => {
    setAddBrokerForm({
      name: broker.name,
      provider: broker.provider,
      api_base_url: broker.api_base_url || 'https://symphonyfintech.com/interactive',
      iris_ws_url: broker.iris_ws_url || 'wss://symphonyfintech.com/marketdata/stream',
      xts_interactive_app_key: broker.xts_interactive_app_key || '',
      xts_interactive_app_secret: broker.xts_interactive_app_secret || '',
      xts_marketdata_app_key: broker.xts_marketdata_app_key || '',
      xts_marketdata_app_secret: broker.xts_marketdata_app_secret || ''
    })
    setEditingBroker(broker)
  }

  const handleDeleteBroker = (id: string) => {
    const broker = brokers.find(b => b.id === id)
    if (!broker) return
    setDeleteConfirm({ type: 'broker', id, label: broker.name, confirmInput: '' })
  }

  const handleDeleteBrokerConfirmed = async () => {
    if (!deleteConfirm) return
    try {
      await fetch(`${API_BASE}/api/brokers/${deleteConfirm.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } })
      setBrokers(prev => prev.filter(b => b.id !== deleteConfirm.id))
      addToast('Broker deleted', 'warning')
    } catch (err) {
      console.error(err)
    } finally {
      setDeleteConfirm(null)
    }
  }

  const handleAddClient = async (brokerId: string) => {
    if (!addClientForm.client_id.trim()) return
    try {
      const res = await fetch(`${API_BASE}/api/brokers/${brokerId}/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...addClientForm, algo_id: addClientForm.algo_id || null }),
      })
      if (res.ok) {
        const result = await res.json()
        setBrokers(prev => prev.map(b => {
          if (b.id === brokerId) {
            return {
              ...b,
              clients: [...b.clients, {
                id: result.id,
                broker_id: brokerId,
                client_id: addClientForm.client_id,
                mapped_user_id: null,
                status: 'disconnected',
                created_at: new Date().toISOString()
              }]
            }
          }
          return b
        }))
        setShowAddClientFor(null)
        setAddClientForm({ client_id: '', session_password: '', ctcl_password: '', algo_id: '' })
        addToast(`Client "${addClientForm.client_id}" added`, 'success')
      }
    } catch (err) {
      console.error(err)
      addToast('Failed to add client', 'error')
    }
  }

  const handleRemoveClient = (brokerId: string, clientId: string) => {
    const broker = brokers.find(b => b.id === brokerId)
    const client = broker?.clients.find(c => c.id === clientId)
    if (!client) return
    setDeleteConfirm({ type: 'client', id: clientId, brokerId, label: client.client_id, confirmInput: '' })
  }

  const handleRemoveClientConfirmed = async () => {
    if (!deleteConfirm || !deleteConfirm.brokerId) return
    try {
      await fetch(`${API_BASE}/api/brokers/${deleteConfirm.brokerId}/clients/${deleteConfirm.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } })
      setBrokers(prev => prev.map(b => b.id === deleteConfirm.brokerId ? { ...b, clients: b.clients.filter(c => c.id !== deleteConfirm.id) } : b))
      addToast('Client removed', 'warning')
    } catch (err) {
      console.error(err)
    } finally {
      setDeleteConfirm(null)
    }
  }

  const handleConnectClient = async (brokerId: string, clientId: string, disconnect = false) => {
    const action = disconnect ? 'disconnect' : 'connect'
    setBrokers(prev => prev.map(b => {
      if (b.id === brokerId) {
        return {
          ...b,
          clients: b.clients.map(c => c.id === clientId ? { ...c, status: disconnect ? 'disconnecting' : 'connecting' } : c)
        }
      }
      return b
    }))

    try {
      const res = await fetch(`${API_BASE}/api/brokers/${brokerId}/clients/${clientId}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (!res.ok && !disconnect) {
        const data = await res.json().catch(() => ({}))
        addToast(data.error || 'Connection failed — check credentials', 'error', 8000)
      }
      // Refresh data from API
      await fetchBrokers()
    } catch (err) {
      console.error(err)
      addToast('Network error connecting client', 'error')
    }
  }

  const handleEditClient = async () => {
    if (!editingClient) return
    if (!editClientForm.session_password && !editClientForm.ctcl_password && !editClientForm.algo_id) {
      addToast('Enter at least one field to update', 'warning')
      return
    }
    try {
      const res = await fetch(
        `${API_BASE}/api/brokers/${editingClient.brokerId}/clients/${editingClient.clientDbId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(editClientForm),
        }
      )
      const data = await res.json()
      if (res.ok) {
        addToast(`Credentials updated for ${editingClient.clientId} — reconnect to apply`, 'success', 6000)
        setEditingClient(null)
        setEditClientForm({ session_password: '', ctcl_password: '', algo_id: '' })
        await fetchBrokers()
      } else {
        addToast(data.error || 'Failed to update credentials', 'error')
      }
    } catch (err) {
      console.error(err)
      addToast('Network error updating credentials', 'error')
    }
  }

  const totalClients = brokers.reduce((acc, b) => acc + b.clients.length, 0)
  const connectedClients = brokers.reduce((acc, b) => acc + b.clients.filter(c => c.status === 'connected').length, 0)

  return (
    <div>
      <div className="page-head">
        <h1>Broker Firm Management <InfoTip term="brokerManagement" placement="bottom" /></h1>
        <p className="page-sub">Manage broker API connections and map multiple Client IDs to Traders.</p>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <button className="btn btn-primary" onClick={() => setShowAddBroker(true)}>
            + Add Broker Firm
          </button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="kpi-strip" style={{ marginBottom: '1rem' }}>
        <div className="kpi-card">
          <div className="kpi-label">Total Firms</div>
          <div className="kpi-value">{brokers.length}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Total Clients</div>
          <div className="kpi-value" style={{ color: 'var(--accent)' }}>{totalClients}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Connected</div>
          <div className="kpi-value" style={{ color: 'var(--green)' }}>{connectedClients}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Disconnected</div>
          <div className="kpi-value" style={{ color: 'var(--text-muted)' }}>{totalClients - connectedClients}</div>
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>⏳ Loading brokers…</span>
        </div>
      ) : (
      <>
      {/* Broker Cards */}
      {brokers.map(broker => (
        <div key={broker.id} className="card" style={{ marginBottom: '1rem' }}>
          {/* Broker Header */}
          <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setExpanded(expanded === broker.id ? null : broker.id)}>
            <div>
              <span className="card-title">⚯ {broker.name}</span>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem', alignItems: 'center' }}>
                <span className="badge badge-target" style={{ fontSize: '0.625rem', textTransform: 'uppercase' }}>{broker.provider}</span>
                <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                  {broker.clients.length} client{broker.clients.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); openEditBroker(broker) }}>Edit</button>
              <button className="btn btn-sm btn-primary" onClick={(e) => { e.stopPropagation(); setShowAddClientFor(broker.id) }}>+ Add Client</button>
              <span style={{ fontSize: '1rem', color: 'var(--text-muted)', transform: expanded === broker.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
            </div>
          </div>

          {/* Expanded Client Table */}
          {expanded === broker.id && (
            <div style={{ padding: '0 1rem 1rem' }}>
              {/* Client List */}
              <DesktopOnly>
              {broker.clients.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)', fontSize: '0.8125rem', fontStyle: 'italic' }}>
                  No clients added yet. Click "+ Add Client" to map a Client ID.
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Client ID</th>
                      <th>Status</th>
                      <th>Net Available<br/><span style={{ fontWeight: 400, fontSize: '0.6rem', color: 'var(--text-muted)', letterSpacing: '0.02em' }}>As per API</span></th>
                      <th>Used Margin<br/><span style={{ fontWeight: 400, fontSize: '0.6rem', color: 'var(--text-muted)', letterSpacing: '0.02em' }}>As per API</span></th>
                      <th>SPAN<br/><span style={{ fontWeight: 400, fontSize: '0.6rem', color: 'var(--text-muted)', letterSpacing: '0.02em' }}>As per API</span></th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {broker.clients.map(client => {
                      const m = margins[client.id]
                      const isConnected = client.status === 'connected'
                      return (
                        <tr key={client.id}>
                          <td><code style={{ fontWeight: 600 }}>{client.client_id}</code></td>
                          <td>
                            <span className={`badge ${
                              client.status === 'connected' ? 'badge-entry'
                              : client.status === 'connecting' || client.status === 'disconnecting' ? 'badge-target'
                              : client.status === 'error' ? 'badge-sl'
                              : 'badge-sl'
                            }`} style={client.status === 'error' ? { background: 'rgba(212,73,90,0.15)', color: '#d4495a', border: '1px solid rgba(212,73,90,0.3)' } : {}}>
                              {client.status === 'error' ? '⚠ Error — Check Credentials' : client.status}
                            </span>
                          </td>
                          {/* Margin columns — only meaningful when connected */}
                          <td style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8125rem' }}>
                            {!isConnected ? <span style={{ color: 'var(--text-muted)' }}>—</span>
                              : m?.loading ? <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>⟳</span>
                              : m?.error ? <span style={{ color: 'var(--red)', fontSize: '0.6875rem' }} title={m.error}>Error</span>
                              : <span style={{ color: (m?.netAvailable ?? 0) > 0 ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>{fmtINR(m?.netAvailable)}</span>}
                          </td>
                          <td style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                            {!isConnected || !m || m.loading ? '—' : fmtINR(m.usedMargin)}
                          </td>
                          <td style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                            {!isConnected || !m || m.loading ? '—' : fmtINR(m.spanMargin)}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
                              {isConnected && (
                                <button className="btn btn-sm btn-ghost" title="Refresh margin"
                                  onClick={() => fetchMargin(broker.id, client.id)}
                                  style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem' }}>⟳</button>
                              )}
                              {client.status === 'connected' ? (
                                <button className="btn btn-sm btn-ghost" style={{ color: 'var(--red)' }}
                                  onClick={() => handleConnectClient(broker.id, client.id, true)}>Disconnect</button>
                              ) : (
                                <button className="btn btn-sm btn-primary"
                                  onClick={() => handleConnectClient(broker.id, client.id, false)}>Connect</button>
                              )}
                              <button className="btn btn-sm btn-ghost"
                                title="Edit credentials"
                                onClick={() => { setEditingClient({ brokerId: broker.id, clientDbId: client.id, clientId: client.client_id }); setEditClientForm({ session_password: '', ctcl_password: '', algo_id: '' }) }}
                                style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}>✏ Edit</button>
                              <button className="btn btn-sm btn-ghost" style={{ color: 'var(--red)' }}
                                onClick={() => handleRemoveClient(broker.id, client.id)}>Remove</button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
              </DesktopOnly>

              <MobileOnly>
                <div className="m-card-list">
                  {broker.clients.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>No clients added yet.</div>
                  ) : broker.clients.map(client => (
                    <MobileCard
                      key={client.id}
                      title={client.client_id}
                      status={{
                        label: client.status,
                        variant: client.status === 'connected' ? 'green' : client.status === 'connecting' ? 'yellow' : 'muted',
                      }}
                      fields={[]}
                      actions={[
                        client.status === 'connected'
                          ? { label: 'Disconnect', variant: 'danger' as const, onClick: () => handleConnectClient(broker.id, client.id, true) }
                          : { label: 'Connect', variant: 'primary' as const, onClick: () => handleConnectClient(broker.id, client.id, false) },
                        { label: '✏ Edit', variant: 'secondary' as const, onClick: () => { setEditingClient({ brokerId: broker.id, clientDbId: client.id, clientId: client.client_id }); setEditClientForm({ session_password: '', ctcl_password: '', algo_id: '' }) } },
                        { label: 'Remove', variant: 'danger' as const, onClick: () => handleRemoveClient(broker.id, client.id) },
                      ]}
                    />
                  ))}
                </div>
              </MobileOnly>

              {/* ─── WebSocket Status & Firm Settings ─── */}
              <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border)' }}>
                {/* WebSocket Status Panel */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                    WebSocket Connections
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.625rem' }}>
                    {/* IRIS WebSocket — per-client for order management */}
                    {(() => {
                      const anyConnected = broker.clients.some(c => c.status === 'connected')
                      // Trading Engine registers connections per-client (by client DB ID), not per-broker-firm.
                      // So we must aggregate WS status across all clients belonging to this broker.
                      const clientStatuses = broker.clients.map(c => wsStatuses[c.id]).filter(Boolean)
                      const irisUp = clientStatuses.some(s => s?.irisConnected)
                      const apolloUp = clientStatuses.some(s => s?.apolloConnected)
                      const lastTick = clientStatuses.find(s => s?.ws?.apollo?.lastTickAt)?.ws?.apollo?.lastTickAt
                      return (
                        <>
                          <div style={{
                            background: 'rgba(14,26,50,0.5)',
                            border: `1px solid ${irisUp ? 'rgba(138,184,42,0.25)' : anyConnected ? 'rgba(212,73,90,0.25)' : 'var(--border)'}`,
                            borderRadius: 'var(--radius-sm)',
                            padding: '0.625rem 0.75rem',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                              <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                                ⚡ IRIS
                                <span style={{
                                  fontSize: '0.5rem', fontWeight: 600, color: '#f59e0b',
                                  background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)',
                                  borderRadius: '3px', padding: '0.05rem 0.25rem', marginLeft: '0.35rem',
                                  verticalAlign: 'middle', letterSpacing: '0.04em',
                                }}>PER-CLIENT</span>
                              </span>
                              <span style={{
                                fontSize: '0.5625rem', fontWeight: 800, letterSpacing: '0.05em',
                                color: irisUp ? '#8ab82a' : anyConnected ? '#d4495a' : '#64748b',
                                background: irisUp ? 'rgba(138,184,42,0.12)' : anyConnected ? 'rgba(212,73,90,0.12)' : 'rgba(100,116,139,0.12)',
                                border: `1px solid ${irisUp ? 'rgba(138,184,42,0.3)' : anyConnected ? 'rgba(212,73,90,0.3)' : 'rgba(100,116,139,0.3)'}`,
                                borderRadius: '3px', padding: '0.1rem 0.35rem',
                                fontFamily: "'JetBrains Mono', monospace",
                              }}>
                                {irisUp ? 'ACTIVE' : anyConnected ? 'DOWN' : 'OFF'}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>
                              Order placement, modifications & real-time order status
                            </div>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.625rem', color: 'var(--text-secondary)', wordBreak: 'break-all', marginTop: '0.25rem' }}>
                              {broker.iris_ws_url || '—'}
                            </div>
                          </div>

                          {/* Apollo WebSocket — SHARED across all clients in this firm */}
                          <div style={{
                            background: 'rgba(14,26,50,0.5)',
                            border: `1px solid ${apolloUp ? 'rgba(138,184,42,0.25)' : anyConnected ? 'rgba(212,73,90,0.25)' : 'var(--border)'}`,
                            borderRadius: 'var(--radius-sm)',
                            padding: '0.625rem 0.75rem',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                              <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                                📡 Apollo
                                <span style={{
                                  fontSize: '0.5rem', fontWeight: 600, color: '#60a5fa',
                                  background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.2)',
                                  borderRadius: '3px', padding: '0.05rem 0.25rem', marginLeft: '0.35rem',
                                  verticalAlign: 'middle', letterSpacing: '0.04em',
                                }}>SHARED</span>
                              </span>
                              <span style={{
                                fontSize: '0.5625rem', fontWeight: 800, letterSpacing: '0.05em',
                                color: apolloUp ? '#8ab82a' : anyConnected ? '#d4495a' : '#64748b',
                                background: apolloUp ? 'rgba(138,184,42,0.12)' : anyConnected ? 'rgba(212,73,90,0.12)' : 'rgba(100,116,139,0.12)',
                                border: `1px solid ${apolloUp ? 'rgba(138,184,42,0.3)' : anyConnected ? 'rgba(212,73,90,0.3)' : 'rgba(100,116,139,0.3)'}`,
                                borderRadius: '3px', padding: '0.1rem 0.35rem',
                                fontFamily: "'JetBrains Mono', monospace",
                              }}>
                                {apolloUp ? 'ACTIVE' : anyConnected ? 'DOWN' : 'OFF'}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>
                              Market Data & Live Rates — shared across all clients
                            </div>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.625rem', color: 'var(--text-secondary)', wordBreak: 'break-all', marginTop: '0.25rem' }}>
                              {broker.apollo_ws_url || '—'}
                            </div>
                            {lastTick && (
                              <div style={{ fontSize: '0.5625rem', color: 'var(--text-muted)', marginTop: '0.25rem', fontFamily: "'JetBrains Mono', monospace" }}>
                                Last tick: {new Date(lastTick).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                              </div>
                            )}
                          </div>

                          {/* Auto-switch row */}
                          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.375rem 0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                                🔄 Auto-Switch
                              </span>
                              <span style={{ fontSize: '0.5625rem', color: 'var(--text-muted)' }}>
                                Reconnect WS automatically if one goes silent
                              </span>
                            </div>
                            <div style={{
                              width: 34, height: 18, borderRadius: 10,
                              background: 'rgba(138,184,42,0.25)',
                              border: '1px solid rgba(138,184,42,0.4)',
                              position: 'relative', cursor: 'default',
                            }}>
                              <div style={{
                                width: 14, height: 14, borderRadius: '50%',
                                background: '#8ab82a',
                                position: 'absolute', top: 1, left: 18,
                                transition: 'left 0.2s',
                              }} />
                            </div>
                          </div>
                        </>
                      )
                    })()}
                  </div>
                </div>

                {/* Firm API Settings */}
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Firm API Settings</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem', fontSize: '0.75rem' }}>
                  <div>
                    <div style={{ color: 'var(--text-muted)', marginBottom: '0.125rem' }}>API Base URL</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", wordBreak: 'break-all', color: 'var(--text-secondary)' }}>{broker.api_base_url || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-muted)', marginBottom: '0.125rem' }}>Session URL</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", wordBreak: 'break-all', color: 'var(--text-secondary)' }}>{broker.session_token_url || '—'}</div>
                  </div>
                </div>
                <div style={{ marginTop: '0.75rem', textAlign: 'right' }}>
                  <button className="btn btn-sm btn-ghost" style={{ color: 'var(--red)' }} onClick={() => handleDeleteBroker(broker.id)}>
                    Delete Broker Firm
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {brokers.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
          <h3 style={{ color: 'var(--text-main)', marginBottom: '0.5rem' }}>No Broker Firms Configured</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginBottom: '1rem' }}>Add a broker firm to start mapping client IDs.</p>
          <button className="btn btn-primary" onClick={() => setShowAddBroker(true)}>Add Broker Firm</button>
        </div>
      )}
      </>
      )}

      {/* ────── Edit Client Credentials Modal ────── */}
      {editingClient && (
        <div className="modal-overlay" onClick={() => setEditingClient(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h3>Edit Credentials — <code style={{ fontSize: '0.875rem', color: 'var(--accent)' }}>{editingClient.clientId}</code></h3>
              <button className="modal-close" onClick={() => setEditingClient(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.75rem', color: '#eab308', marginBottom: '1rem', lineHeight: 1.5 }}>
                ⚠ Leave a field blank to keep the existing password. Updating will disconnect the client — reconnect after saving.
              </p>
              <div className="cfg-grid">
                <div className="cfg-field">
                  <label className="cfg-label">Session Password</label>
                  <input className="cfg-input" type="password" placeholder="Leave blank to keep current"
                    value={editClientForm.session_password}
                    onChange={e => setEditClientForm({ ...editClientForm, session_password: e.target.value })} />
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">CTCL Password</label>
                  <input className="cfg-input" type="password" placeholder="Leave blank to keep current"
                    value={editClientForm.ctcl_password}
                    onChange={e => setEditClientForm({ ...editClientForm, ctcl_password: e.target.value })} />
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Algo ID (Optional)</label>
                  <input className="cfg-input" placeholder="Leave blank to keep current"
                    value={editClientForm.algo_id}
                    onChange={e => setEditClientForm({ ...editClientForm, algo_id: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditingClient(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEditClient}
                disabled={!editClientForm.session_password && !editClientForm.ctcl_password && !editClientForm.algo_id}>
                Save & Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ────── Add Client Modal ────── */}
      {showAddClientFor && (
        <div className="modal-overlay" onClick={() => setShowAddClientFor(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Client ID</h3>
              <button className="modal-close" onClick={() => setShowAddClientFor(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="cfg-grid">
                <div className="cfg-field">
                  <label className="cfg-label">Client ID</label>
                  <input className="cfg-input" placeholder="e.g. GREEK13"
                    style={{ textTransform: 'uppercase' }}
                    value={addClientForm.client_id}
                    onChange={e => setAddClientForm({...addClientForm, client_id: e.target.value.toUpperCase()})} />
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Session Password</label>
                  <input className="cfg-input" type="password" placeholder="e.g. greek@123"
                    value={addClientForm.session_password}
                    onChange={e => setAddClientForm({...addClientForm, session_password: e.target.value})} />
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">CTCL Password</label>
                  <input className="cfg-input" type="password" placeholder="e.g. a555555555"
                    value={addClientForm.ctcl_password}
                    onChange={e => setAddClientForm({...addClientForm, ctcl_password: e.target.value})} />
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Algo ID (Optional)</label>
                  <input className="cfg-input" placeholder="e.g. 12345"
                    value={addClientForm.algo_id}
                    onChange={e => setAddClientForm({...addClientForm, algo_id: e.target.value})} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowAddClientFor(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => handleAddClient(showAddClientFor)}
                disabled={!addClientForm.client_id}>
                Add Client & Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ────── Add / Edit Broker Firm Modal ────── */}
      {(showAddBroker || editingBroker) && (
        <div className="modal-overlay" onClick={() => { setShowAddBroker(false); setEditingBroker(null) }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h3>{editingBroker ? 'Edit Broker Firm' : 'Add Broker Firm'}</h3>
              <button className="modal-close" onClick={() => { setShowAddBroker(false); setEditingBroker(null) }}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
              <div className="cfg-grid">
                <div className="cfg-field">
                  <label className="cfg-label">Firm Name</label>
                  <input className="cfg-input" placeholder="e.g. Shri Vijayshree Investment Services"
                    value={addBrokerForm.name}
                    onChange={e => setAddBrokerForm({...addBrokerForm, name: e.target.value})} />
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">API Provider</label>
                  <select className="cfg-input" value={addBrokerForm.provider} disabled>
                    <option value="xts">XTS (Symphony Fintech)</option>
                  </select>
                </div>
              </div>

              <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border)' }}>
                <p style={{ fontSize: '0.75rem', color: '#60a5fa', marginBottom: '0.75rem' }}>Configure XTS Interactive & Market Data endpoints.</p>
                <div className="cfg-grid">
                  <div className="cfg-field" style={{ gridColumn: '1 / -1' }}>
                    <label className="cfg-label" style={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Interactive API Base URL</label>
                    <input className="cfg-input" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}
                      value={addBrokerForm.api_base_url} onChange={e => setAddBrokerForm({...addBrokerForm, api_base_url: e.target.value})} />
                  </div>
                  <div className="cfg-field">
                    <label className="cfg-label" style={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Interactive App Key</label>
                    <input className="cfg-input" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}
                      value={addBrokerForm.xts_interactive_app_key} onChange={e => setAddBrokerForm({...addBrokerForm, xts_interactive_app_key: e.target.value})} />
                  </div>
                  <div className="cfg-field">
                    <label className="cfg-label" style={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Interactive App Secret</label>
                    <input className="cfg-input" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}
                      value={addBrokerForm.xts_interactive_app_secret} onChange={e => setAddBrokerForm({...addBrokerForm, xts_interactive_app_secret: e.target.value})} />
                  </div>
                  <div className="cfg-field" style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}>
                    <label className="cfg-label" style={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Market Data WS URL</label>
                    <input className="cfg-input" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}
                      value={addBrokerForm.iris_ws_url} onChange={e => setAddBrokerForm({...addBrokerForm, iris_ws_url: e.target.value})} />
                  </div>
                  <div className="cfg-field">
                    <label className="cfg-label" style={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Market Data App Key</label>
                    <input className="cfg-input" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}
                      value={addBrokerForm.xts_marketdata_app_key} onChange={e => setAddBrokerForm({...addBrokerForm, xts_marketdata_app_key: e.target.value})} />
                  </div>
                  <div className="cfg-field">
                    <label className="cfg-label" style={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Market Data App Secret</label>
                    <input className="cfg-input" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}
                      value={addBrokerForm.xts_marketdata_app_secret} onChange={e => setAddBrokerForm({...addBrokerForm, xts_marketdata_app_secret: e.target.value})} />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => { setShowAddBroker(false); setEditingBroker(null) }}>Cancel</button>
              <button className="btn btn-primary" onClick={editingBroker ? handleEditBroker : handleAddBroker}
                disabled={!addBrokerForm.name}>
                {editingBroker ? 'Save Changes' : 'Create Firm'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Delete / Remove Confirmation Modal ──────────────────── */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" style={{ maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title" style={{ color: 'var(--red)' }}>
                {deleteConfirm.type === 'broker' ? '🗑 Delete Broker Firm' : '🗑 Remove Client ID'}
              </h3>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Warning message */}
              <div style={{
                background: 'rgba(212,73,90,0.1)',
                border: '1px solid rgba(212,73,90,0.3)',
                borderRadius: 'var(--radius-sm)',
                padding: '0.75rem',
                fontSize: '0.8125rem',
                lineHeight: 1.5,
                color: 'var(--text-secondary)',
              }}>
                {deleteConfirm.type === 'broker'
                  ? <>This will permanently delete the broker firm and <strong>all its client IDs</strong>. This action cannot be undone.</>
                  : <>This will permanently remove the client ID and all its credentials. This action cannot be undone.</>
                }
              </div>

              {/* Type-to-confirm input */}
              <div className="cfg-field">
                <label className="cfg-label">
                  Type <code style={{ color: 'var(--red)', background: 'rgba(212,73,90,0.12)', padding: '0.1em 0.4em', borderRadius: '3px' }}>{deleteConfirm.label}</code> to confirm
                </label>
                <input
                  className="cfg-input"
                  autoFocus
                  placeholder={deleteConfirm.label}
                  value={deleteConfirm.confirmInput}
                  onChange={e => setDeleteConfirm({ ...deleteConfirm, confirmInput: e.target.value })}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && deleteConfirm.confirmInput === deleteConfirm.label) {
                      deleteConfirm.type === 'broker' ? handleDeleteBrokerConfirmed() : handleRemoveClientConfirmed()
                    }
                  }}
                  style={{ borderColor: deleteConfirm.confirmInput === deleteConfirm.label ? 'var(--green)' : undefined }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button
                className="btn"
                style={{ background: 'var(--red)', color: '#fff', opacity: deleteConfirm.confirmInput === deleteConfirm.label ? 1 : 0.4, cursor: deleteConfirm.confirmInput === deleteConfirm.label ? 'pointer' : 'not-allowed' }}
                disabled={deleteConfirm.confirmInput !== deleteConfirm.label}
                onClick={() => deleteConfirm.type === 'broker' ? handleDeleteBrokerConfirmed() : handleRemoveClientConfirmed()}
              >
                {deleteConfirm.type === 'broker' ? 'Delete Broker' : 'Remove Client'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
